import { registerFunctionalComponent } from 'js/lib/components/component'

registerFunctionalComponent('.js-c-form-input--file-wrapper', 'InputFile', InputFile)

const MAX_FILENAME_LENGTH = 48
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.heic', '.heif', '.tif', '.tiff', '.avif']
const FILE_ROW_CLASS = 'tw-flex tw-items-center tw-justify-self-start tw-gap-3 tw-mt-2 tw-p-2 tw-border-solid tw-border tw-border-neutral-400 tw-rounded-lg tw-bg-neutral-50 tw-text-sm'
const ICON_COL_CLASS = 'tw-shrink-0 tw-flex tw-items-center'
const ICON_CLASS = 'tw-w-5 tw-h-5 tw-opacity-80'
const DETAILS_COL_CLASS = 'tw-flex tw-flex-col tw-min-w-0 tw-flex-1'
const FILE_NAME_CLASS = 'tw-truncate tw-font-medium'
const FILE_SIZE_CLASS = 'tw-text-xs tw-text-neutral-600'
const REMOVE_BUTTON_CLASS = 'tw-shrink-0 tw-text-red-600 tw-text-xs tw-border-0 tw-bg-transparent tw-cursor-pointer tw-whitespace-nowrap tw-p-1'

function parseOptionalInt(value) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) ? parsed : null
}

function InputFile(component) {
  const wrapper = component.element
  const input = wrapper.querySelector('.js-c-form-input--file')
  const maxSizeBytes = parseOptionalInt(input.dataset.maxFileSize)
  const maxFiles = parseOptionalInt(input.dataset.maxFiles)
  const fileImageIconUrl = input.dataset.fileImageIconUrl
  const fileTextIconUrl = input.dataset.fileTextIconUrl
  const fileQuestionIconUrl = input.dataset.fileQuestionIconUrl
  const trigger = wrapper.querySelector('.js-c-form-input--file-trigger')

  const fileListContainer = wrapper.querySelector('.js-c-form-input--file-list')
  const fileItemsList = wrapper.querySelector('.js-c-form-input--file-items')
  const fileCountEl = wrapper.querySelector('.js-c-form-input--file-count')
  const fileMap = new Map()

  function fileKey(file) {
    return `${file.name}|${file.size}|${file.lastModified}`
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  function truncateFilename(filename) {
    if (filename.length <= MAX_FILENAME_LENGTH) return filename
    return `${filename.slice(0, MAX_FILENAME_LENGTH - 1)}…`
  }

  function isImageFile(file) {
    if (file.type && file.type.startsWith('image/')) return true
    const lowercaseName = file.name.toLowerCase()
    return IMAGE_EXTENSIONS.some(ext => lowercaseName.endsWith(ext))
  }

  function isPdfFile(file) {
    if (file.type === 'application/pdf') return true
    return file.name.toLowerCase().endsWith('.pdf')
  }

  function iconUrlFor(file) {
    if (isImageFile(file)) return fileImageIconUrl
    if (isPdfFile(file)) return fileTextIconUrl
    return fileQuestionIconUrl
  }

  function syncInputFiles() {
    try {
      const dt = new DataTransfer()
      fileMap.forEach(file => dt.items.add(file))
      input.files = dt.files
    } catch {
      return
    }
  }

  function buildFileListItem(file, key) {
    const li = document.createElement('li')
    li.className = FILE_ROW_CLASS

    const iconCol = document.createElement('span')
    iconCol.className = ICON_COL_CLASS

    const icon = document.createElement('img')
    icon.className = ICON_CLASS
    icon.src = iconUrlFor(file)
    icon.alt = ''
    icon.setAttribute('aria-hidden', 'true')
    iconCol.appendChild(icon)

    const detailCol = document.createElement('span')
    detailCol.className = DETAILS_COL_CLASS

    const filename = document.createElement('span')
    filename.className = FILE_NAME_CLASS
    filename.textContent = truncateFilename(file.name)
    filename.title = file.name

    const filesize = document.createElement('span')
    filesize.className = FILE_SIZE_CLASS
    filesize.textContent = formatFileSize(file.size)

    detailCol.appendChild(filename)
    detailCol.appendChild(filesize)

    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = REMOVE_BUTTON_CLASS
    removeButton.textContent = 'Remove'
    removeButton.setAttribute('aria-label', `Remove ${file.name}`)
    removeButton.addEventListener('click', () => {
      fileMap.delete(key)
      syncInputFiles()
      renderFileList()
    })

    li.appendChild(iconCol)
    li.appendChild(detailCol)
    li.appendChild(removeButton)

    return li
  }

  function validateFileSizes(files) {
    if (!maxSizeBytes) return true

    const oversized = files.filter(file => file.size > maxSizeBytes)
    if (oversized.length === 0) return true

    const maxSizeMB = Math.round(maxSizeBytes / 1024 / 1024)
    const names = oversized.map(file => `${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`)
    window.alert(`The following files exceed the maximum allowed size of ${maxSizeMB}MB:\n\n${names.join('\n')}\n\nPlease remove them and try again.`)
    input.value = ''
    return false
  }

  function renderFileList() {
    if (!fileListContainer) return

    if (fileMap.size === 0) {
      fileListContainer.classList.add('tw-hidden')
      return
    }

    fileListContainer.classList.remove('tw-hidden')
    fileCountEl.textContent = `(${fileMap.size})`
    fileItemsList.innerHTML = ''

    fileMap.forEach((file, key) => {
      fileItemsList.appendChild(buildFileListItem(file, key))
    })
  }

  component.useHandler(() => {
    const handleFileChange = () => {
      const newFiles = Array.from(input.files)
      if (!validateFileSizes(newFiles)) return

      if (input.multiple) {
        newFiles.forEach(file => {
          fileMap.set(fileKey(file), file)
        })
      } else {
        fileMap.clear()
        if (newFiles[0]) {
          fileMap.set(fileKey(newFiles[0]), newFiles[0])
        }
      }

      if (input.multiple && maxFiles && maxFiles > 0 && fileMap.size > maxFiles) {
        const kept = Array.from(fileMap.values()).slice(0, maxFiles)
        fileMap.clear()
        kept.forEach(file => fileMap.set(fileKey(file), file))
        window.alert(`You can upload up to ${maxFiles} files.`)
      }

      syncInputFiles()
      renderFileList()
    }

    input.addEventListener('change', handleFileChange)

    const handleTriggerClick = (event) => {
      event.preventDefault()
      input.click()
    }

    if (trigger) {
      trigger.addEventListener('click', handleTriggerClick)
    }

    return () => {
      input.removeEventListener('change', handleFileChange)
      if (trigger) {
        trigger.removeEventListener('click', handleTriggerClick)
      }
    }
  })
}
